import { useState, useRef, useEffect, useCallback } from 'react';
import * as Cesium from 'cesium';
import { useCustomDataSource } from './common/useCustomDataSource';
import { usePickTerrainPosition } from './common/useCesiumInteraction';
import { disableCameraControls, enableCameraControls } from '../utils/cesiumHelpers';
import { makePolylineWidthProperty } from '../utils/cesiumEntityFactory';
import { generateId } from '../utils/idGeneration';
import { serializeSketch } from '../utils/sketchGeometry';
// Resample distance (screen px) while dragging. Below this the move is
// ignored, so a slow drag doesn't pack thousands of near-identical points.
const MIN_SAMPLE_PX = 4;
// Erase recompute step (screen px) — keep erasing responsive without
// re-projecting every stroke on every sub-pixel move.
const ERASE_STEP_PX = 3;
// Minimum on-screen diameter for the pen cursor dot so thin pens stay visible.
const PEN_CURSOR_MIN_PX = 10;
export const useSketch = ({ viewer, isActive, projectId, mode, color, width, opacity: penOpacity, eraserSize }) => {
    const dataSourceRef = useCustomDataSource(viewer, 'sketch');
    const pickTerrainPosition = usePickTerrainPosition(viewer);
    // Reactive mirror of the stroke list for consumers outside the hook (the
    // save modal's count, the Unsaved Drawings row). Colour follows the first
    // stroke, the same one serializeForSave stamps on the saved artifact.
    const [strokeCount, setStrokeCount] = useState(0);
    const [canvasColor, setCanvasColor] = useState(color);
    const [isDrawing, setIsDrawing] = useState(false);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    // Canvas-level view state. The whole sketch is one unsaved item, so
    // visibility rides the data source's `show` flag and opacity rides each
    // stroke's material alpha. Canvas opacity is a view multiplier over each
    // stroke's own opacity, never a replacement for it.
    const [isVisible, setIsVisible] = useState(true);
    const [opacity, setOpacityState] = useState(1);
    const strokesRef = useRef([]);
    const activeStrokeRef = useRef(null);
    const isErasingRef = useRef(false);
    const lastSamplePxRef = useRef(null);
    const cursorElRef = useRef(null);
    // Undo / redo: each entry is a full snapshot of the canvas BEFORE a
    // mutating gesture (one pen stroke, one erase drag, or a Clear).
    const undoStackRef = useRef([]);
    const redoStackRef = useRef([]);
    // Erase-gesture bookkeeping: snapshot at press, whether anything changed.
    const preGestureRef = useRef(null);
    const gestureChangedRef = useRef(false);
    // Latest tool settings, mirrored into refs so the Cesium handler (bound
    // once per activation) always reads current values without re-binding.
    const colorRef = useRef(color);
    const widthRef = useRef(width);
    const modeRef = useRef(mode);
    const eraserSizeRef = useRef(eraserSize);
    const isActiveRef = useRef(isActive);
    const pickRef = useRef(pickTerrainPosition);
    // Two distinct opacities: the pen setting a new stroke is inked with, and
    // the canvas multiplier the Unsaved Drawings slider drives.
    const penOpacityRef = useRef(penOpacity);
    const opacityRef = useRef(1);
    useEffect(() => { colorRef.current = color; }, [color]);
    useEffect(() => { widthRef.current = width; }, [width]);
    useEffect(() => { penOpacityRef.current = penOpacity; }, [penOpacity]);
    useEffect(() => { modeRef.current = mode; }, [mode]);
    useEffect(() => { eraserSizeRef.current = eraserSize; }, [eraserSize]);
    useEffect(() => { isActiveRef.current = isActive; }, [isActive]);
    useEffect(() => { pickRef.current = pickTerrainPosition; }, [pickTerrainPosition]);
    // Called after every mutation of strokesRef so the reactive mirror never
    // drifts from the canvas.
    const syncCanvasState = useCallback(() => {
        const strokes = strokesRef.current;
        setStrokeCount(strokes.length);
        if (strokes.length > 0)
            setCanvasColor(strokes[0].color);
    }, []);
    const bumpHistoryFlags = useCallback(() => {
        setCanUndo(undoStackRef.current.length > 0);
        setCanRedo(redoStackRef.current.length > 0);
    }, []);
    const captureSnapshot = useCallback(() => strokesRef.current.map(s => ({ color: s.color, width: s.width, opacity: s.opacity, points: s.points })), []);
    const addStrokeEntity = useCallback((color, width, strokeOpacity, points) => {
        const ds = dataSourceRef.current;
        if (!ds || !viewer || viewer.isDestroyed())
            return null;
        return ds.entities.add({
            polyline: {
                positions: points.slice(),
                width: makePolylineWidthProperty(width, viewer),
                material: new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(color).withAlpha(strokeOpacity * opacityRef.current)),
                clampToGround: true,
            },
        });
    }, [viewer]);
    // Replace the live canvas with a snapshot, rebuilding entities.
    const restoreSnapshot = useCallback((snap) => {
        const ds = dataSourceRef.current;
        if (ds)
            strokesRef.current.forEach(s => ds.entities.remove(s.entity));
        const restored = [];
        for (const s of snap) {
            const entity = addStrokeEntity(s.color, s.width, s.opacity, s.points);
            if (entity)
                restored.push({ id: generateId('sketch'), color: s.color, width: s.width, opacity: s.opacity, points: s.points, entity });
        }
        strokesRef.current = restored;
        syncCanvasState();
    }, [addStrokeEntity, syncCanvasState]);
    const pushUndo = useCallback((snap) => {
        undoStackRef.current.push(snap);
        redoStackRef.current = [];
        bumpHistoryFlags();
    }, [bumpHistoryFlags]);
    const undo = useCallback(() => {
        if (undoStackRef.current.length === 0)
            return;
        redoStackRef.current.push(captureSnapshot());
        restoreSnapshot(undoStackRef.current.pop());
        bumpHistoryFlags();
    }, [captureSnapshot, restoreSnapshot, bumpHistoryFlags]);
    const redo = useCallback(() => {
        if (redoStackRef.current.length === 0)
            return;
        undoStackRef.current.push(captureSnapshot());
        restoreSnapshot(redoStackRef.current.pop());
        bumpHistoryFlags();
    }, [captureSnapshot, restoreSnapshot, bumpHistoryFlags]);
    // Clear button — undoable (pushes the pre-clear canvas onto the stack).
    const clearAll = useCallback(() => {
        if (strokesRef.current.length === 0)
            return;
        pushUndo(captureSnapshot());
        const ds = dataSourceRef.current;
        if (ds)
            strokesRef.current.forEach(s => ds.entities.remove(s.entity));
        strokesRef.current = [];
        activeStrokeRef.current = null;
        syncCanvasState();
    }, [pushUndo, captureSnapshot, syncCanvasState]);
    // Hard reset — NOT undoable. Used after Save, on Discard, and on project
    // switch, where bringing the canvas back via undo would be wrong.
    const resetCanvas = useCallback(() => {
        const ds = dataSourceRef.current;
        if (ds)
            strokesRef.current.forEach(s => ds.entities.remove(s.entity));
        strokesRef.current = [];
        activeStrokeRef.current = null;
        undoStackRef.current = [];
        redoStackRef.current = [];
        syncCanvasState();
        bumpHistoryFlags();
    }, [bumpHistoryFlags, syncCanvasState]);
    // Load a saved sketch's strokes into the live canvas for editing. Clears
    // the current canvas + history, rehydrates lat/lng → world positions, and
    // returns a sentinel id so the edit flow's `if (unsavedId)` gate passes.
    // Callers hand over parseSketchGeometry output, so the style fields are
    // already coerced and clamped here.
    const loadStrokes = useCallback((strokes) => {
        const ds = dataSourceRef.current;
        if (ds)
            strokesRef.current.forEach(s => ds.entities.remove(s.entity));
        undoStackRef.current = [];
        redoStackRef.current = [];
        const loaded = [];
        for (const s of strokes || []) {
            const pts = (s.points || []).map(p => Cesium.Cartesian3.fromDegrees(p.lng, p.lat));
            if (pts.length < 2)
                continue;
            const entity = addStrokeEntity(s.color, s.width, s.opacity, pts);
            if (entity)
                loaded.push({ id: generateId('sketch'), color: s.color, width: s.width, opacity: s.opacity, points: pts, entity });
        }
        strokesRef.current = loaded;
        syncCanvasState();
        bumpHistoryFlags();
        return 'sketch-edit';
    }, [addStrokeEntity, bumpHistoryFlags, syncCanvasState]);
    // Serialize the current canvas for persistence: project each stroke's
    // world positions back to lat/lng and pack them into the geometry_json
    // schema. Returns null when there's nothing to save. The artifact's
    // top-level colour (the folder-tree dot) takes the first stroke's colour.
    const serializeForSave = useCallback(() => {
        const strokes = strokesRef.current;
        if (strokes.length === 0)
            return null;
        const data = strokes.map(s => ({
            tool: 'pen',
            color: s.color,
            width: s.width,
            opacity: s.opacity,
            points: s.points.map(pos => {
                const c = Cesium.Cartographic.fromCartesian(pos);
                return { lat: Cesium.Math.toDegrees(c.latitude), lng: Cesium.Math.toDegrees(c.longitude) };
            }),
        }));
        return { geometryJson: serializeSketch(data), color: strokes[0].color, strokeCount: strokes.length };
    }, []);
    // Drop the canvas when the project changes so one project's sketch never
    // bleeds onto another (mirrors the path tool's project-switch reset).
    const prevProjectIdRef = useRef(null);
    useEffect(() => {
        if (prevProjectIdRef.current !== null && prevProjectIdRef.current !== projectId) {
            resetCanvas();
        }
        prevProjectIdRef.current = projectId;
    }, [projectId, resetCanvas]);
    // ⌘Z / Ctrl+Z to undo, ⌘⇧Z / Ctrl+Y to redo (parity with the polygon tool).
    useEffect(() => {
        if (!isActive)
            return;
        const onKey = (e) => {
            const t = e.target;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable))
                return;
            const mod = e.metaKey || e.ctrlKey;
            if (mod && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                if (e.shiftKey)
                    redo();
                else
                    undo();
            }
            else if (mod && e.key.toLowerCase() === 'y') {
                e.preventDefault();
                redo();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isActive, undo, redo]);
    // Freehand capture + erase: own ScreenSpaceEventHandler, attached only
    // while the Sketch tool is armed (same pattern as usePolygonDrawing). The
    // camera is suspended for the duration of a gesture only — between gestures
    // you can pan/zoom normally; while dragging, the drag draws or erases.
    useEffect(() => {
        if (!viewer || viewer.isDestroyed() || !isActive)
            return;
        const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
        // Tool cursor — a DOM circle that follows the pointer. Pen: a filled dot
        // in the current colour at (≈) the stroke width. Eraser: a white ring at
        // the eraser diameter. box-sizing:border-box so the element's width IS the
        // visible outer diameter (and, for the eraser, the erase radius).
        const cursorEl = document.createElement('div');
        cursorEl.style.cssText =
            'position:absolute;box-sizing:border-box;pointer-events:none;border:2px solid rgba(255,255,255,0.95);' +
                'box-shadow:0 0 0 1px rgba(0,0,0,0.55);border-radius:50%;transform:translate(-50%,-50%);' +
                'display:none;z-index:5;left:0;top:0;';
        viewer.container.appendChild(cursorEl);
        cursorElRef.current = cursorEl;
        const updateCursor = (x, y) => {
            const cur = cursorElRef.current;
            if (!cur)
                return;
            if (modeRef.current === 'eraser') {
                const d = eraserSizeRef.current;
                cur.style.width = `${d}px`;
                cur.style.height = `${d}px`;
                cur.style.backgroundColor = 'transparent';
                cur.style.borderColor = 'rgba(255,255,255,0.95)';
            }
            else {
                const d = Math.max(widthRef.current, PEN_CURSOR_MIN_PX);
                cur.style.width = `${d}px`;
                cur.style.height = `${d}px`;
                // Fade the fill, not the element: the white ring has to stay findable
                // on dark terrain, and element opacity would take it down too.
                cur.style.backgroundColor = Cesium.Color
                    .fromCssColorString(colorRef.current)
                    .withAlpha(penOpacityRef.current)
                    .toCssColorString();
                cur.style.borderColor = 'rgba(255,255,255,0.95)';
            }
            cur.style.left = `${x}px`;
            cur.style.top = `${y}px`;
            cur.style.display = 'block';
        };
        const hideCursor = () => { if (cursorElRef.current)
            cursorElRef.current.style.display = 'none'; };
        const canvas = viewer.scene.canvas;
        canvas.addEventListener('mouseleave', hideCursor);
        const finishStroke = () => {
            const stroke = activeStrokeRef.current;
            activeStrokeRef.current = null;
            lastSamplePxRef.current = null;
            if (!viewer.isDestroyed())
                enableCameraControls(viewer);
            setIsDrawing(false);
            if (!stroke)
                return;
            const ds = dataSourceRef.current;
            // A press with no real drag (<2 sampled points) is a stray click, not
            // a stroke — discard it rather than leaving an invisible 1-point line.
            if (stroke.points.length < 2) {
                if (ds)
                    ds.entities.remove(stroke.entity);
                return;
            }
            // Freeze the live CallbackProperty into a constant array so finished
            // strokes don't pay a per-frame allocation forever.
            if (stroke.entity.polyline) {
                stroke.entity.polyline.positions = new Cesium.ConstantProperty(stroke.points.slice());
            }
            // History: snapshot the canvas BEFORE adding this stroke, so undo drops it.
            pushUndo(captureSnapshot());
            strokesRef.current.push(stroke);
            syncCanvasState();
        };
        const eraseAt = (screenPos) => {
            const ds = dataSourceRef.current;
            if (!ds || viewer.isDestroyed())
                return;
            const scene = viewer.scene;
            // Radius IS the visible circle's radius — no width padding, so the
            // erase matches the cursor ring rather than reaching past it.
            const radius = eraserSizeRef.current / 2;
            const rSq = radius * radius;
            // Where does the segment from screen point A→B cross the circle edge?
            // Returns the parametric t in (0,1), or null if it doesn't cross.
            const crossT = (a, b) => {
                const dx = b.x - a.x, dy = b.y - a.y;
                const fx = a.x - screenPos.x, fy = a.y - screenPos.y;
                const qa = dx * dx + dy * dy;
                if (qa === 0)
                    return null;
                const qb = 2 * (fx * dx + fy * dy);
                const qc = fx * fx + fy * fy - rSq;
                const disc = qb * qb - 4 * qa * qc;
                if (disc < 0)
                    return null;
                const sq = Math.sqrt(disc);
                for (const t of [(-qb - sq) / (2 * qa), (-qb + sq) / (2 * qa)]) {
                    if (t >= 0 && t <= 1)
                        return t;
                }
                return null;
            };
            let changed = false;
            const next = [];
            for (const stroke of strokesRef.current) {
                const n = stroke.points.length;
                const screens = stroke.points.map(p => Cesium.SceneTransforms.worldToWindowCoordinates(scene, p));
                const inside = screens.map(sp => {
                    if (!sp)
                        return false;
                    const dx = sp.x - screenPos.x, dy = sp.y - screenPos.y;
                    return dx * dx + dy * dy <= rSq;
                });
                if (!inside.some(Boolean)) {
                    next.push(stroke);
                    continue;
                }
                changed = true;
                ds.entities.remove(stroke.entity);
                const runs = [];
                let run = [];
                const flush = () => { if (run.length >= 2)
                    runs.push(run); run = []; };
                const boundary = (i, j) => {
                    const sa = screens[i], sb = screens[j];
                    if (!sa || !sb)
                        return null;
                    const t = crossT(sa, sb);
                    if (t == null)
                        return null;
                    return Cesium.Cartesian3.lerp(stroke.points[i], stroke.points[j], t, new Cesium.Cartesian3());
                };
                for (let i = 0; i < n; i++) {
                    if (!inside[i]) {
                        if (i > 0 && inside[i - 1]) {
                            const b = boundary(i - 1, i);
                            if (b)
                                run.push(b);
                        }
                        run.push(stroke.points[i]);
                    }
                    else {
                        if (i > 0 && !inside[i - 1]) {
                            const b = boundary(i - 1, i);
                            if (b)
                                run.push(b);
                        }
                        flush();
                    }
                }
                flush();
                for (const r of runs) {
                    const entity = addStrokeEntity(stroke.color, stroke.width, stroke.opacity, r);
                    if (entity)
                        next.push({ id: generateId('sketch'), color: stroke.color, width: stroke.width, opacity: stroke.opacity, points: r, entity });
                }
            }
            if (changed) {
                strokesRef.current = next;
                syncCanvasState();
                gestureChangedRef.current = true;
            }
        };
        handler.setInputAction((event) => {
            if (!isActiveRef.current)
                return;
            const ds = dataSourceRef.current;
            if (!ds)
                return;
            if (modeRef.current === 'eraser') {
                disableCameraControls(viewer);
                isErasingRef.current = true;
                setIsDrawing(true);
                // Capture the canvas at the start of the erase drag; commit one undo
                // entry for the whole gesture on release (if anything changed).
                preGestureRef.current = captureSnapshot();
                gestureChangedRef.current = false;
                eraseAt(event.position);
                lastSamplePxRef.current = { x: event.position.x, y: event.position.y };
                return;
            }
            const pos = pickRef.current(event.position);
            if (!pos)
                return;
            disableCameraControls(viewer);
            setIsDrawing(true);
            const points = [pos];
            const strokeColor = colorRef.current;
            const strokeWidth = widthRef.current;
            const strokeOpacity = penOpacityRef.current;
            const entity = ds.entities.add({
                polyline: {
                    positions: new Cesium.CallbackProperty(() => points.slice(), false),
                    width: makePolylineWidthProperty(strokeWidth, viewer),
                    material: new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(strokeColor).withAlpha(strokeOpacity * opacityRef.current)),
                    clampToGround: true,
                },
            });
            activeStrokeRef.current = { id: generateId('sketch'), color: strokeColor, width: strokeWidth, opacity: strokeOpacity, points, entity };
            lastSamplePxRef.current = { x: event.position.x, y: event.position.y };
        }, Cesium.ScreenSpaceEventType.LEFT_DOWN);
        handler.setInputAction((event) => {
            const px = event.endPosition;
            updateCursor(px.x, px.y);
            if (modeRef.current === 'eraser') {
                if (!isErasingRef.current)
                    return;
                const last = lastSamplePxRef.current;
                if (last) {
                    const dx = px.x - last.x;
                    const dy = px.y - last.y;
                    if (dx * dx + dy * dy < ERASE_STEP_PX * ERASE_STEP_PX)
                        return;
                }
                eraseAt(px);
                lastSamplePxRef.current = { x: px.x, y: px.y };
                return;
            }
            const stroke = activeStrokeRef.current;
            if (!stroke)
                return;
            const last = lastSamplePxRef.current;
            if (last) {
                const dx = px.x - last.x;
                const dy = px.y - last.y;
                if (dx * dx + dy * dy < MIN_SAMPLE_PX * MIN_SAMPLE_PX)
                    return;
            }
            const pos = pickRef.current(px);
            if (!pos)
                return;
            stroke.points.push(pos);
            lastSamplePxRef.current = { x: px.x, y: px.y };
        }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        handler.setInputAction(() => {
            if (isErasingRef.current) {
                isErasingRef.current = false;
                lastSamplePxRef.current = null;
                if (!viewer.isDestroyed())
                    enableCameraControls(viewer);
                setIsDrawing(false);
                // One undo entry per erase gesture, only if it actually removed anything.
                if (gestureChangedRef.current && preGestureRef.current) {
                    pushUndo(preGestureRef.current);
                }
                preGestureRef.current = null;
                gestureChangedRef.current = false;
                return;
            }
            finishStroke();
        }, Cesium.ScreenSpaceEventType.LEFT_UP);
        return () => {
            // Tearing down mid-gesture (tool switch while pressed) — finalize so the
            // camera is never left disabled and a half-stroke isn't orphaned.
            if (activeStrokeRef.current)
                finishStroke();
            isErasingRef.current = false;
            canvas.removeEventListener('mouseleave', hideCursor);
            handler.destroy();
            if (cursorElRef.current) {
                cursorElRef.current.remove();
                cursorElRef.current = null;
            }
            if (!viewer.isDestroyed())
                enableCameraControls(viewer);
        };
    }, [viewer, isActive, pushUndo, captureSnapshot, addStrokeEntity, syncCanvasState]);
    // Hiding the canvas is one flag on the data source, so it covers strokes
    // drawn while hidden too. Re-applied when the viewer (and with it the data
    // source) is rebuilt.
    useEffect(() => {
        const ds = dataSourceRef.current;
        if (ds)
            ds.show = isVisible;
    }, [viewer, isVisible]);
    const toggleVisibility = useCallback(() => {
        setIsVisible(prev => !prev);
    }, []);
    // Canvas opacity: a view multiplier over each stroke's own opacity, so a
    // 20% stroke stays the faintest one at every slider position. It does not
    // touch the pen setting, so the next stroke drawn is inked at the pen's
    // opacity rather than the slider's.
    const setOpacity = useCallback((value) => {
        const alpha = Math.max(0, Math.min(1, value));
        opacityRef.current = alpha;
        setOpacityState(alpha);
        strokesRef.current.forEach(s => {
            if (s.entity.polyline) {
                s.entity.polyline.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(s.color).withAlpha(s.opacity * alpha));
            }
        });
    }, []);
    return {
        strokeCount,
        canvasColor,
        isDrawing,
        canUndo,
        canRedo,
        isVisible,
        toggleVisibility,
        opacity,
        setOpacity,
        clearAll,
        resetCanvas,
        undo,
        redo,
        serializeForSave,
        loadStrokes,
    };
};
